import React, { createContext, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../components/Icon';
import { usePref } from '../hooks';
import s from './ToolDock.module.css';

// The right-hand tool panel, part of the frame like the drawer: a slim rail of tool icons down the right edge and,
// when one is open, its panel as a rounded sheet beside the page. A page docks its tools with <ToolDock tools={…}/>
// (they portal into the frame's slot); pages without tools have no rail at all.
export const ToolSlot = createContext({ slot: null, setDocked: () => {} });

export function useToolSlot() {
  const [slot, setSlot] = useState(null);
  const [docked, setDocked] = useState(false);
  return { value: { slot, setDocked }, ref: setSlot, docked };
}

/** tools: [{ key, icon, label, render: () => node }]. `id` keeps the open tool per page. */
export default function ToolDock({ id, tools, initial = null }) {
  const { slot, setDocked } = useContext(ToolSlot);
  const [open, setOpen] = usePref(`tools.${id}`, initial ?? tools[0]?.key ?? null);
  useEffect(() => { setDocked(true); return () => setDocked(false); }, [setDocked]);
  if (!slot) return null;
  const tool = tools.find((t) => t.key === open) || null;
  return createPortal(
    <>
      {tool ? (
        <aside className={s.panel} aria-label={tool.label}>
          <div className={s.head}><Icon name={tool.icon} size={16} /><span>{tool.label}</span><span className="spacer" />
            <button className={s.close} onClick={() => setOpen(null)} aria-label="Close panel" title="Close"><Icon name="chevrons-right" size={16} /></button>
          </div>
          <div className={s.body}>{tool.render()}</div>
        </aside>
      ) : null}
      <nav className={s.rail} aria-label="Tools">
        {tools.map((t) => (
          <button key={t.key} className={s.tool} data-on={open === t.key || undefined} onClick={() => setOpen(open === t.key ? null : t.key)} title={t.label} aria-label={t.label} aria-pressed={open === t.key}>
            <Icon name={t.icon} size={19} />
          </button>
        ))}
      </nav>
    </>,
    slot,
  );
}
